import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const PrivacyPolicy = () => {
  const [mounted, setMounted] = useState(false);
  const location = useLocation();
  const { from, step, formData } = location.state || { from: '/signup', step: 1, formData: null };

  useEffect(() => {
    setMounted(true);
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className={`max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 transform transition-all duration-800 ease-out ${
        mounted ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
      }`}>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
          <Link 
            to={from}
            state={{ step: step, formData: formData }}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-300"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Signup
          </Link>
        </div>

        <div className="prose prose-green max-w-none">
          <p className="text-sm text-gray-500 mb-4">
            Effective Date: December 24, 2025 | Last Updated: December 24, 2025
          </p>

          <div className="bg-green-50 p-4 rounded-lg mb-6">
            <p className="text-gray-700">
              Welcome to Pengravity CSS/PMS (“we,” “us,” “our,” or “Pengravity”). These Terms and Conditions govern your access to and use of our website <a href="https://www.pengravity.com" className="text-green-600 hover:underline">www.pengravity.com</a> and our Learning Management System (the “LMS”). By using our platform, you agree to comply with these terms in full.
            </p>
            <p className="text-gray-700 mt-2">
              If you have any questions, contact us at: <span className="font-semibold">Email: pengravity@gmail.com</span> | <span className="font-semibold">Phone/WhatsApp: +92 325 2666328</span>
            </p>
          </div>

          <section className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">1. Acceptance of Terms</h2>
            <p className="text-gray-600">By accessing or using Pengravity CSS/PMS, you confirm that you have read, understood, and agree to these Terms and Conditions, including our Privacy Policy. If you do not agree, you must not use the platform.</p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">2. Eligibility</h2>
            <ul className="list-disc pl-6 text-gray-600 space-y-1">
              <li>Pengravity CSS/PMS is intended for users 18 years and older.</li>
              <li>By using the platform, you confirm that you meet this age requirement and have the legal capacity to enter into these terms.</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">3. User Accounts</h2>
            <ul className="list-disc pl-6 text-gray-600 space-y-1">
              <li>You may need to create an account to access courses and services.</li>
              <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
              <li>You must provide accurate, complete, and current information during registration.</li>
              <li>You agree to notify us immediately of any unauthorized use of your account.</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">4. Permitted Use</h2>
            <p className="text-gray-600">You agree to use the LMS only for lawful, educational purposes. Prohibited actions include, but are not limited to:</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-1 mt-2">
              <li>Copying, distributing, or sharing content without permission</li>
              <li>Attempting to interfere with the platform's functionality</li>
              <li>Impersonating other users or staff members</li>
              <li>Using the LMS for illegal, harmful, or fraudulent purposes</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">5. Intellectual Property</h2>
            <ul className="list-disc pl-6 text-gray-600 space-y-1">
              <li>All content, lectures, notes, graphics, software, and materials on Pengravity CSS/PMS are the intellectual property of Pengravity and are protected under Pakistani copyright and intellectual property laws.</li>
              <li>Unauthorized reproduction, distribution, or derivative work creation is strictly prohibited without written consent.</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">6. Payment and Refunds</h2>
            <ul className="list-disc pl-6 text-gray-600 space-y-1">
              <li>All course fees are processed securely via trusted third-party payment gateways.</li>
              <li>Refunds, if applicable, will be subject to our Refund Policy.</li>
              <li>Any fraudulent or unauthorized activity related to payments may result in legal action and account termination.</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">7. Limitation of Liability</h2>
            <ul className="list-disc pl-6 text-gray-600 space-y-1">
              <li>Pengravity CSS/PMS and its affiliates are not liable for any direct, indirect, incidental, or consequential damages arising from the use of the LMS.</li>
              <li>This includes, but is not limited to, technical failures, content errors, or interruptions in service.</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">8. Disclaimers</h2>
            <ul className="list-disc pl-6 text-gray-600 space-y-1">
              <li>While we strive to provide accurate, updated, and high-quality content, we do not guarantee outcomes from the use of the platform.</li>
              <li>The LMS is provided "as is," and you use it at your own risk.</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">9. Termination</h2>
            <p className="text-gray-600">Pengravity reserves the right to suspend or terminate your account at any time without notice for violations of these Terms and Conditions or misuse of the LMS.</p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">10. Modifications to Terms</h2>
            <ul className="list-disc pl-6 text-gray-600 space-y-1">
              <li>We may update these Terms and Conditions periodically.</li>
              <li>Changes will be posted on this page with an updated "Last Updated" date.</li>
              <li>Continued use of the LMS after updates constitutes acceptance of the revised terms.</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">11. Governing Law</h2>
            <p className="text-gray-600">These Terms and Conditions are governed by the laws of Pakistan. Any disputes arising from these terms will be subject to the exclusive jurisdiction of courts in Pakistan.</p>
          </section>

          <section className="mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">12. Contact Information</h2>
            <p className="text-gray-600">For questions regarding these Terms and Conditions, please contact:</p>
            <div className="bg-gray-50 p-4 rounded-lg mt-2">
              <p className="text-gray-700"><span className="font-semibold">Pengravity Education System</span></p>
              <p className="text-gray-700">Email: pengravity@gmail.com</p>
              <p className="text-gray-700">Phone/WhatsApp: +92 325 2666328</p>
              <p className="text-gray-700">Website: www.pengravity.com</p>
            </div>
          </section>
        </div>

        <div className="mt-8 text-center">
          <Link 
            to={from}
            state={{ step: step, formData: formData }}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl hover:from-green-600 hover:to-emerald-700 transform hover:scale-105 transition-all duration-300"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Signup
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;